<template>
	<p class="fraud-evidence">
		<template v-for="(seg, i) in segments">
			<link-tag-view
				v-if="seg && typeof seg === 'object'"
				:key="'t' + i"
				:label="seg.label"
				:href="seg.href || null"
				:click="seg.click || null"
			/>
			<span v-else :key="'s' + i">{{ seg }}</span>
		</template>
	</p>
</template>

<script>
import LinkTagView        from './LinkTagView';
import EmployeeWindow     from '../windows/EmployeeWindow';
import InvoiceWindow      from '../windows/InvoiceWindow';
import StampWindow        from '../windows/StampWindow';
import ContactWindow      from '../windows/ContactWindow';
import OrderController    from '../OrderController';

/**
 * Renders alarm evidence as one flowing Danish sentence with factual pivots
 * woven inline as tags. Two tag styles (see LinkTagView):
 *
 *   link (blue) — employees, invoices, orders, stamps, e-conomic URLs.
 *                 Click opens the relevant side-window.
 *   data (gray) — dates, leave types, phone numbers, free-text values.
 *                 Non-interactive, just visually set apart from prose.
 *
 * Templates never wrap tag labels in parentheses — the tag itself already
 * stands out from the surrounding prose.
 *
 * Phase 2 will replace hard-coded templates with a server-supplied evidence
 * descriptor.
 */
export default {
	components: { LinkTagView },
	props: {
		alarm: { type: Object, required: true }
	},
	computed: {
		ev() { return this.alarm.evidence || {}; },

		segments() {
			const a  = this.alarm;
			const ev = this.ev;

			// ----- date/time helpers -----

			const fmtDate = (d) => {
				if (!d) return null;
				const dt = new Date(d);
				return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('da-DK');
			};
			const fmtDateTime = (d) => {
				if (!d) return null;
				const dt = new Date(d);
				return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString('da-DK');
			};

			// ----- tag builders -----
			// Clickable (blue) tags — return {label, click} or {label, href}.
			// Data (gray) tags — return {label} only (LinkTagView renders the
			// no-handler case as --data).
			// Return null when the underlying id/value is missing.

			const actor = (a.actor_id && a.actor_id !== 0)
				? { label: a.actor_name || 'Medarbejder #' + a.actor_id, click: () => this._openEmployee(a.actor_id) }
				: 'En medarbejder';

			const about = a.about_employee_id
				? { label: a.about_employee_name || 'medarbejder #' + a.about_employee_id, click: () => this._openEmployee(a.about_employee_id) }
				: 'medarbejderen';

			const leaveType = ev.leave_type_name   ? { label: ev.leave_type_name } : null;
			const dateTag   = (d) => { const s = fmtDate(d);     return s ? { label: s } : null; };
			const timeTag   = (d) => { const s = fmtDateTime(d); return s ? { label: s } : null; };

			const invoice = ev.invoice_id
				? { label: 'faktura ' + (ev.num || ev.post_ref || '#' + ev.invoice_id), click: () => this._openInvoice(ev.invoice_id) }
				: null;

			const order = ev.order_id
				? { label: 'ordre #' + ev.order_id, click: () => this._openOrder(ev.order_id) }
				: null;

			const stamp = ev.stamp_id
				? { label: 'stempel #' + ev.stamp_id, click: () => this._openStamp(ev.stamp_id) }
				: null;

			const economic = ev.economic_url_booked
				? { label: 'e-conomic', href: ev.economic_url_booked }
				: (ev.economic_url_draft ? { label: 'e-conomic', href: ev.economic_url_draft } : null);

			const customer        = ev.customer_name         ? { label: ev.customer_name }         : null;
			const matchedCustomer = ev.matched_customer_name ? { label: ev.matched_customer_name } : null;
			const phone           = ev.normalized_phone      ? { label: ev.normalized_phone }      : null;
			const total           = ev.local_total           ? { label: ev.local_total + ' kr' }   : null;
			const count           = ev.count != null         ? { label: String(ev.count) }         : null;
			const hours           = ev.hours != null         ? { label: ev.hours + ' timer' }      : null;

			// Money formatter - dot-grouped Danish-style.
			const money = (v) => v == null ? null : { label: this._fmtMoney(v) + ' kr' };

			// Local customer (Moovr contact). Linkable via _openContact when contact_id present.
			const localCustomerOf = (name, contactId) => {
				if (!name) return null;
				return contactId
					? { label: name, click: () => this._openContact(contactId) }
					: { label: name };
			};

			// e-conomic customer. Linkable to e-conomic customer page when customerNumber known.
			const economicCustomerOf = (name, customerNumber) => {
				if (!name) return null;
				return customerNumber
					? { label: name, href: 'https://secure.e-conomic.com/secure/customer/' + customerNumber }
					: { label: name };
			};

			// e-conomic invoice ref tag - always external link.
			const economicInvoiceOf = (postRef, urlOverride) => {
				if (!postRef) return null;
				return {
					label: 'faktura ' + postRef,
					href:  urlOverride || ('https://secure.e-conomic.com/secure/sales/booked/' + postRef)
				};
			};

			// Order tag tied to a specific order, more useful than the
			// generic `order` const above which keys off ev.order_id only.
			const orderOf = (id, num) => id
				? { label: 'ordre #' + (num || id), click: () => this._openOrder(id) }
				: null;

			// Employee tag for any employee_id (sender / order owner).
			const employeeOf = (id, name) => {
				if (!id || id === 0) return null;
				return { label: name || ('Medarbejder #' + id), click: () => this._openEmployee(id) };
			};

			// Inline date range — returns a list of segments, spread into
			// the sentence. Used instead of prose because the "fra X til Y"
			// structure is common to many rules.
			const range = (from, to) => {
				const fromTag = dateTag(from);
				const toTag   = dateTag(to);
				if (fromTag && toTag && fromTag.label !== toTag.label) return [' fra ', fromTag, ' til ', toTag];
				if (fromTag) return [' for ', fromTag];
				return [];
			};

			const whenTag = timeTag(a.created);

			// ----- per-rule templates -----

			let segs;

			switch (a.rule) {
				case 'leave.deleted_without_replacement':
					segs = [
						actor, ' slettede ', leaveType || 'fravær', ' for ', about,
						...range(ev.from_date || ev.leave_date, ev.to_date),
						' efter perioden var afholdt.'
					];
					break;

				case 'leave.self_modification':
					segs = [
						actor, ' ændrede sin egen registrering af ', leaveType || 'fravær',
						...range(ev.from_date || ev.leave_date, ev.to_date),
						' den ', whenTag, '.'
					];
					break;

				case 'leave.retroactive_edit':
					segs = [
						actor, ' ændrede ', about, 's ', leaveType || 'fravær',
						...range(ev.from_date || ev.leave_date, ev.to_date),
						' efter det normale ændringsvindue.'
					];
					break;

				case 'leave.range_shortened':
					segs = [
						actor, ' forkortede ', about, 's ', leaveType || 'fraværsperiode',
						...range(ev.from_date || ev.leave_date, ev.to_date), '.'
					];
					break;

				case 'leave.bulk_spike':
					segs = [
						actor, ' ændrede ', count || 'flere',
						' fraværsposter inden for kort tid omkring ', whenTag, '.'
					];
					break;

				case 'leave.stamp_conflict':
					segs = [
						about, ' har ', stamp || 'et stempel',
						' på ', dateTag(ev.leave_date) || 'en dag',
						' hvor de samtidig er registreret med ', leaveType || 'fravær', '.'
					];
					break;

				case 'hours.edited_after_transfer':
					segs = [
						actor, ' ændrede ', about, 's timer',
						ev.leave_date ? [' for ', dateTag(ev.leave_date)] : [],
						' efter de var overført til løn den ', whenTag, '.'
					].flat();
					break;

				case 'hours.leave_overlap':
					segs = [
						about, ' har ', hours || 'arbejdstimer',
						' på ', dateTag(ev.date || ev.leave_date) || 'en dag',
						' hvor der også er registreret ', leaveType || 'fravær', '.'
					];
					break;

				case 'financial.invoice_disappeared': {
					const senderTag = employeeOf(ev.sender_employee_id, ev.sender_employee_name)
					                || employeeOf(ev.order_owner_id,    ev.order_owner_name);
					const econInv   = economicInvoiceOf(ev.post_ref, ev.economic_url_booked);
					const localCust = localCustomerOf(ev.local_customer_name, ev.local_customer_id);
					segs = [
						orderOf(ev.order_id, ev.order_num) || 'En ordre',
						' blev sendt til e-conomic som ', econInv || 'en faktura',
						senderTag ? [' af ', senderTag] : [],
						ev.sent_at ? [' den ', timeTag(ev.sent_at)] : [],
						' (lokalt ', localCust || 'kunde',
						money(ev.local_total) ? [', ', money(ev.local_total)] : [],
						'), men fakturaen findes ikke længere i e-conomic - hverken bogført eller som kladde.'
					].flat();
					break;
				}

				case 'financial.invoice_substituted': {
					const senderTag = employeeOf(ev.sender_employee_id, ev.sender_employee_name)
					                || employeeOf(ev.order_owner_id,    ev.order_owner_name);
					const econInv   = economicInvoiceOf(ev.post_ref, ev.economic_url_booked);
					const localCust = localCustomerOf(ev.local_customer_name, ev.local_customer_id);
					const econCust  = economicCustomerOf(ev.economic_customer_name, ev.economic_customer_number);
					const diffs = [];
					if (ev.diff && ev.diff.customer) {
						diffs.push(' kunde: ', localCust || { label: ev.diff.customer.local },
						           ' (lokalt) ≠ ', econCust || { label: ev.diff.customer.economic }, ' (e-conomic)');
					}
					if (ev.diff && ev.diff.total) {
						if (diffs.length) diffs.push(' · ');
						diffs.push(' beløb: ', money(ev.diff.total.local), ' (lokalt) ≠ ',
						           money(ev.diff.total.economic), ' (e-conomic)');
					}
					segs = [
						orderOf(ev.order_id, ev.order_num) || 'En ordre',
						' blev sendt til e-conomic som ', econInv || 'en faktura',
						senderTag ? [' af ', senderTag] : [],
						', men det bogførte adskiller sig fra ordren:',
						...diffs,
						'. Bookingen er sandsynligvis blevet ændret i e-conomic efter den lokale postering.'
					].flat();
					break;
				}

				case 'financial.credit_note_unexplained': {
					const econInv   = economicInvoiceOf(ev.post_ref || ev.num, ev.economic_url_booked);
					const localCust = localCustomerOf(ev.local_customer_name, ev.local_customer_id);
					const amount    = Math.abs(ev.economic_total || ev.local_total || 0);
					segs = [
						'Kreditnota ', econInv || { label: '#' + (ev.invoice_id || '?') },
						amount > 0 ? [' på ', money(amount)] : [],
						' blev bogført i e-conomic',
						localCust ? [' for ', localCust] : [],
						' uden en tilsvarende lokal annullering, refundering eller frakobling - typisk skal en kreditnota matche en lokal handling.'
					].flat();
					break;
				}

				case 'financial.invoice_ping_pong': {
					const orderTag = orderOf(ev.order_id, ev.order_num) || 'En ordre';
					const total = (ev.attach_count || 0) + (ev.detach_count || 0);
					segs = [
						orderTag, ' har fået sin fakturatilknytning ændret ',
						{ label: String(total) + ' gange' },
						' (', { label: String(ev.attach_count || 0) + ' tilkoblinger' },
						', ',  { label: String(ev.detach_count || 0) + ' frakoblinger' }, ')',
						ev.first_event && ev.last_event
							? [' mellem ', timeTag(ev.first_event), ' og ', timeTag(ev.last_event)]
							: [],
						' - en normal faktureringsproces gør dette én gang.'
					].flat();
					break;
				}

				case 'financial.after_hours_send': {
					const econInv   = economicInvoiceOf(ev.invoice_post_ref);
					const orderTag  = orderOf(ev.order_id);
					const win       = ev.window
						? { label: ev.window.start + '-' + ev.window.end }
						: { label: '07:00-19:00' };
					segs = [
						actor, ' sendte ',
						econInv || 'en faktura',
						orderTag ? [' for ', orderTag] : [],
						' kl. ', timeTag(ev.occurred_at) || (ev.local_time ? { label: ev.local_time } : whenTag),
						' - uden for arbejdsvinduet ', win, '.'
					].flat();
					break;
				}

				case 'financial.duplicate_local_customer':
					segs = [
						customer || 'En ny kontakt',
						' blev oprettet med telefonnummer ', phone || 'ukendt nr.',
						', som matcher ', matchedCustomer || 'en eksisterende kontakt',
						' under et andet navn - klassisk identitets-spoofing-mønster.'
					];
					break;

				case 'financial.invoiced_without_reference': {
					const senderTag = employeeOf(ev.sender_employee_id, ev.sender_employee_name)
					                || employeeOf(ev.order_owner_id,    ev.order_owner_name);
					const orderTag  = orderOf(ev.order_id, ev.order_num) || 'En ordre';
					const localCust = localCustomerOf(ev.local_customer_name, ev.local_customer_id);

					// Render up to 2 candidates from each strategy as inline links to e-conomic.
					const candTags = (list) => (list || []).slice(0, 2).map(c =>
						economicInvoiceOf(c.num) || { label: '#' + (c.num || '?') }
					);

					const strong = candTags(ev.candidates_amount_and_name);
					const weakA  = candTags(ev.candidates_amount_only);
					const weakN  = candTags(ev.candidates_name_only);

					const verdict = strong.length
						? ' En e-conomic-faktura matcher både kunde og beløb - sandsynligvis bare brudt sammenkobling: '
						: (weakA.length || weakN.length)
							? ' Svag korrelation - bør gennemgås manuelt: '
							: ' Ingen matchende e-conomic-faktura fundet inden for ±' + (ev.window_days || 14) + ' dage - lokal markering mangler dækning.';

					const candList = [];
					if (strong.length) {
						strong.forEach((t, i) => { if (i) candList.push(', '); candList.push(t); });
					} else if (weakA.length || weakN.length) {
						const all = [...weakA, ...weakN];
						all.forEach((t, i) => { if (i) candList.push(', '); candList.push(t); });
					}

					segs = [
						orderTag, ' er lokalt markeret som faktureret ',
						senderTag ? ['af ', senderTag, ' '] : [],
						'(', localCust || 'ukendt kunde',
						money(ev.local_total) ? [', ', money(ev.local_total)] : [],
						'), men har ingen reference til e-conomic.',
						verdict,
						...candList,
						candList.length ? '.' : ''
					].flat();
					break;
				}

				default:
					segs = [ev.summary || 'Hændelse registreret den ', whenTag, '.'];
			}

			// Drop null/undefined/empty segments so skipped optional fragments
			// don't leave stray spaces or render as empty tags.
			return segs.filter(s => s !== null && s !== undefined && s !== '');
		}
	},

	methods: {
		_openEmployee(id) {
			if (!id) return;
			this.$store.state.windowStack.push(EmployeeWindow, { id, form: true });
		},
		_openInvoice(id) {
			if (!id) return;
			this.$store.state.windowStack.push(InvoiceWindow, { id, form: true });
		},
		_openOrder(id) {
			if (!id) return;
			OrderController.open(this, { id });
		},
		async _openContact(id) {
			if (!id) return;
			try {
				const value = (await this.axios.get('/contacts/' + id + '/')).data;
				this.$store.state.windowStack.push(ContactWindow, {
					value,
					submit() { /* read-only jump from fraud context */ }
				});
			} catch (err) {
				alert('Kunne ikke åbne kontakt: ' + (err.message || 'ukendt fejl'));
			}
		},
		_fmtMoney(v) {
			const n = Number(v);
			if (!isFinite(n)) return String(v);
			// Danish formatting: . as thousand separator, , as decimal.
			return n.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
		},
		async _openStamp(id) {
			if (!id) return;
			try {
				const ent = (await this.axios.get('/stamps/' + id + '/')).data;
				this.$store.state.windowStack.push(StampWindow, {
					ent,
					async submit(updated) {
						// Read-only jump from a fraud context — swallow edits
						// rather than mutate source records from here.
						return updated;
					}
				});
			} catch (err) {
				alert('Kunne ikke åbne stempel: ' + (err.message || 'ukendt fejl'));
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.fraud-evidence {
	font-size: 14px;
	line-height: 1.7;
	color: #ffffff;
	margin: 0;
}
</style>
